import { useTranslation } from 'react-i18next'

import {
  Alert,
  Box,
  PrimaryButton,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'

import { AlertActions } from '~/components/common/AlertActions'
import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { useMemberDisplayName } from '~/hooks/member/useMemberDisplayName'
import type { BulkApprovalFlow } from '~/pages/SpotBidding/hooks/useBulkApprovalFlow'

type Props = {
  flow: Pick<
    BulkApprovalFlow,
    'pendingCount' | 'notApproverCount' | 'disabledReason' | 'open' | 'outcome'
  >
  /** 入口の補足文 (通常入札 / ブロック入札で「1件」 の単位が違う)。 */
  hint: string
}

/** 結果の失敗一覧が長くなっても画面を埋めないための高さ上限 (一括取下の結果 `BulkWithdrawResultBanner` と共通)。 */
export const FAILURE_LIST_MAX_HEIGHT = '240px'

/**
 * まとめて承認 (AC-BSP-118) の入口と直近の実行結果。 承認状態バナーの上に置く
 * (通常入札タブ・ブロック入札タブ共通。 design `spot-bulk-approve.js` の `renderPanel`)。
 *
 * - 承認待ちが 0 件の時、 または全件が現在ユーザーの承認者でない案件の時は入口を出さない
 *   (承認者でない人に「まとめて承認できます」 は出さない。 緊急対応の承認は案件ごとの
 *   承認状態バナーから行う。 AC-BSP-098。 alert-placement §3)。 入札締切を過ぎた受渡日の
 *   承認待ちは呼び出し元が数えないので、 締切後は入口ごと出ない (AC-BSP-095 / AC-BSP-099)
 * - 確定済みワークフロー・明細未解決は、 非表示ではなく disabled + 理由の tooltip
 *   (AC-BSP-099。 単件の承認ボタンと同じ見せ方で、 理由は単件と同じ policy が決める)
 * - 結果は承認件数と、 承認できなかった案件の理由を案件ごとに出す。 RPC error の時は
 *   承認件数を断定しない
 */
export const BulkApprovePanel = ({
  flow,
  hint,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()
  const resolveDisplayName = useMemberDisplayName()
  const { pendingCount, notApproverCount, disabledReason, open, outcome } = flow
  const showEntry = pendingCount > 0 && notApproverCount < pendingCount
  if (!showEntry && outcome === undefined) return null

  const openButton = (
    <PrimaryButton
      disabled={disabledReason !== undefined}
      onClick={disabledReason === undefined ? open : undefined}
      size="sm"
    >
      {t('page:biddingCommon.bulkApprove.action')}
    </PrimaryButton>
  )

  return (
    // `flexShrink={0}`: 呼び出し元は高さ拘束された column flex で、 既定だと押し潰されて
    // DS `Alert` の overflow で中身が切れる (承認状態バナーと同じ)。
    <VStack align="stretch" flexShrink={0} gap={2}>
      {outcome?.kind === 'done' && outcome.approvedCount > 0 && (
        <Alert
          status="success"
          title={t('page:biddingCommon.bulkApprove.result.approved', {
            count: outcome.approvedCount,
          })}
        />
      )}
      {outcome?.kind === 'done' && outcome.failures.length > 0 && (
        <Alert
          description={
            <Box
              as="ul"
              overflowY="auto"
              maxHeight={FAILURE_LIST_MAX_HEIGHT}
              margin={0}
              paddingInlineStart={5}
            >
              {outcome.failures.map((f) => (
                <Box key={f.id} as="li">
                  <Text as="span" fontWeight="semibold">
                    {t('page:biddingCommon.bulkApprove.result.failureItem', {
                      title: f.title,
                      requester: resolveDisplayName(f.requestedBy) ?? '—',
                    })}
                  </Text>
                  {f.lines.map((line) => (
                    <Text key={line}>{line}</Text>
                  ))}
                  {f.hiddenCount > 0 && (
                    <Text>
                      {t('page:biddingCommon.submitConfirm.violationMore', {
                        count: f.hiddenCount,
                      })}
                    </Text>
                  )}
                </Box>
              ))}
            </Box>
          }
          status="warning"
          title={t('page:biddingCommon.bulkApprove.result.failed', {
            count: outcome.failures.length,
          })}
        />
      )}
      {outcome?.kind === 'rpcError' && (
        <Alert
          description={
            <VStack align="stretch" gap={1}>
              <Text>{outcome.message}</Text>
              <Text>
                {t('page:biddingCommon.bulkApprove.result.rpcErrorBody')}
              </Text>
            </VStack>
          }
          status="error"
          title={t('page:biddingCommon.bulkApprove.result.rpcErrorTitle')}
        />
      )}
      {showEntry && (
        <Alert
          description={hint}
          status="warning"
          title={t('page:biddingCommon.bulkApprove.entry.title', {
            count: pendingCount,
          })}
        >
          <AlertActions>
            {/* 押せない理由は ButtonTooltip で出す (action-reason.md の区分 2)。 DS ボタンは disabled でも
                aria-disabled で focus を受けるので、 包みに tabIndex を足すと Tab が 2 回止まる。
                包みを止まり先にするかは ButtonTooltip が子を見て決める。 */}
            <ButtonTooltip content={disabledReason}>{openButton}</ButtonTooltip>
          </AlertActions>
        </Alert>
      )}
    </VStack>
  )
}
